import { Plus, X } from "lucide-react";
import { useMemo } from "react";

import { CarregandoLista } from "@/components/carregando";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

import ResumoPagoComanda from "../cardapio-mesa/ResumoPagoComanda";
import ItensCancelados from "../mesas-lancar/ItensCancelados";
import RodadaComanda from "../cardapio-mesa/RodadaComanda";
import { useComandaMesa } from "./useComandaMesa";

const dinheiro = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
// Rodadas do garçom dividem o pedido da mesa: o pedido_id sozinho repete
const chaveRodada = (r, i) => `${r.pedido_id || "mesa"}-${r.cliente_id || "sem-dono"}-${r.criado_em || ""}-${i}`;

/**
 * Comanda da mesa no painel (olhinho do cartão em Mesas, dono 25/09/2026):
 * as mesmas rodadas da comanda do celular do Mesa QR — quem pediu, foto,
 * situação de cada prato no KDS e o que já foi pago —, mais quem lançou e os
 * adicionais. Só leitura; "Adicionar itens" leva para a tela de lançar.
 */
export default function ComandaMesaDialog({ mesa, onClose, onAdicionar, allProducts, fotoDe }) {
  const { dados, erro } = useComandaMesa(mesa?.id);
  const escuro = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  // Produto lançado sem foto no carrinho: usa a do cadastro
  const fotoDoProduto = useMemo(
    () => new Map((allProducts || []).map((p) => [String(p.id), p.photo_url || null])),
    [allProducts],
  );
  const rodadas = useMemo(() => (dados?.comanda?.rodadas || []).map((r) => ({
    ...r,
    itens: (r.itens || []).map((it) => (it.foto ? it : { ...it, foto: fotoDoProduto.get(String(it.produto_id)) || null })),
  })), [dados, fotoDoProduto]);
  const comanda = dados?.comanda;
  const nome = mesa ? (mesa.nome || `Mesa ${mesa.numero}`) : "";

  return (
    <Dialog open={!!mesa} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent hideClose data-e2e="comanda-mesa-painel"
        className="sm:max-w-lg p-0 gap-0 max-h-[90dvh] flex flex-col overflow-hidden">
        <div className="bg-primary text-primary-foreground px-5 pt-5 pb-4">
          <div className="flex items-start gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-widest opacity-80">Comanda da</p>
              <DialogTitle className="text-2xl font-black truncate">{nome.toUpperCase()}</DialogTitle>
            </div>
            <button type="button" onClick={onClose} aria-label="Fechar"
              className="ml-auto -mr-1 p-1.5 rounded-full hover:bg-white/15 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <DialogDescription className="sr-only">
            Tudo que foi pedido na mesa, com quem pediu, quem lançou e a situação de cada prato.
          </DialogDescription>
          <div className="mt-2 flex items-end justify-between gap-3">
            <span className="text-sm opacity-90 truncate">
              {mesa?.cliente ? `${mesa.cliente} · ` : ""}
              {/* "0 envios" antes da comanda chegar enganava: o número só entra com os dados */}
              {dados && `${rodadas.length} ${rodadas.length === 1 ? "envio" : "envios"}`}
            </span>
            <span className="text-2xl font-black tabular-nums shrink-0" data-e2e="comanda-total">
              {dinheiro(comanda?.total ?? mesa?.total)}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-muted/20">
          {erro ? (
            <p className="text-sm text-red-500 text-center py-8">{erro}</p>
          ) : !dados ? (
            <CarregandoLista linhas={3} colunas={1} />
          ) : rodadas.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {(comanda?.cancelados || []).length ? "Todos os produtos desta mesa foram cancelados." : "Nada lançado nesta mesa ainda."}
            </p>
          ) : (
            rodadas.map((r, i) => <RodadaComanda key={chaveRodada(r, i)} rodada={r} fotoDe={fotoDe} darkMode={escuro} produtos={allProducts} />)
          )}
          <ItensCancelados produtos={allProducts} itens={(comanda?.cancelados || []).map((c) => ({
            deleted: true, name: c.nome, quantity: c.quantidade, deleted_at: c.deleted_at, _lancado_por: c.lancado_por,
            photo_url: c.foto || fotoDoProduto.get(String(c.produto_id)) || null,
            id: c.produto_id, // o cadastro diz se é por peso (30/09/2026)
          }))} />
          <ResumoPagoComanda comanda={comanda} darkMode={escuro} />
        </div>

        <div className="border-t px-4 py-3 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Fechar</Button>
          <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold" onClick={() => onAdicionar(mesa)}
            data-e2e="comanda-adicionar">
            <Plus className="w-4 h-4 mr-1" /> Adicionar itens
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
